// /Volumes/KINGSTON/new-react-project/the-ultimate-guide/src/components/Review/syncTime.js
// Purpose: convert between "video time" (seconds in the current POV file)
// and "match time" (a shared timeline across POVs).
//
// Convention we will use everywhere:
// syncOffsetSec = (videoTimeSec - matchTimeSec) at the sync marker.
// So:
//   matchTimeSec = videoTimeSec - syncOffsetSec
//   videoTimeSec = matchTimeSec + syncOffsetSec

const clampNumber = (n, fallback = 0) => {
    const x = Number(n);
    return Number.isFinite(x) ? x : fallback;
  };
  
  /**
   * Convert a POV's current video time to the shared match time.
   * @param {number} videoTimeSec - currentTime of the <video> element (seconds)
   * @param {number} syncOffsetSec - offset stored on that POV video doc
   * @returns {number} matchTimeSec
   */
  export function toMatchTimeSec(videoTimeSec, syncOffsetSec) {
    const vt = clampNumber(videoTimeSec, 0);
    const off = clampNumber(syncOffsetSec, 0);
    // avoid negatives; match time should never be below 0
    return Math.max(0, vt - off);
  }
  
  /**
   * Convert a shared match time to the correct seek time for a POV.
   * @param {number} matchTimeSec - the shared timeline time (seconds)
   * @param {number} syncOffsetSec - offset stored on that POV video doc
   * @returns {number} videoTimeSec
   */
  export function toVideoTimeSec(matchTimeSec, syncOffsetSec) {
    const mt = clampNumber(matchTimeSec, 0);
    const off = clampNumber(syncOffsetSec, 0);
    // avoid negatives; video time should never be below 0
    return Math.max(0, mt + off);
  }
  
  /**
   * Optional helper: compute the syncOffsetSec given the chosen sync marker.
   * If the user marks "Round 1 starts when timer hits 1:00" at the current video time,
   * this returns the offset that should be stored on the POV doc.
   *
   * @param {number} markerVideoTimeSec - the POV's current video time at the marker
   * @param {number} markerMatchTimeSec - where that marker should land on the shared timeline (usually 0)
   * @returns {number} syncOffsetSec
   */
  export function computeSyncOffsetSec(markerVideoTimeSec, markerMatchTimeSec = 0) {
    const vt = clampNumber(markerVideoTimeSec, 0);
    const mt = clampNumber(markerMatchTimeSec, 0);
    // offset = video - match
    return vt - mt;
  }
  
  /**
   * Optional helper: stable equality check for offsets/time math (floating point safety)
   */
  export function nearlyEqual(a, b, eps = 0.05) {
    const x = clampNumber(a, 0);
    const y = clampNumber(b, 0);
    return Math.abs(x - y) <= eps;
  }
  